import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface TextareaEditableProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value: string;
  className?: string;
  textClassName?: string;
  textareaClassName?: string;
  maxRows?: number;
  minRows?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  // 外部控制编辑状态的props
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

/**
 * 可编辑的文本区域组件
 */
export const TextareaEditable: React.FC<TextareaEditableProps> = ({
  value,
  onChange,
  className,
  textClassName,
  textareaClassName,
  maxRows = 3,
  minRows = 1,
  placeholder = '',
  disabled = false,
  // 外部控制编辑状态的props
  isEditing: externalIsEditing,
  onEditingChange,
  ...rest
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 使用内部状态，但当外部提供isEditing时优先使用外部值
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [calculatedRows, setCalculatedRows] = useState(minRows);

  // 确定当前的编辑状态（优先使用外部控制）
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  // 当外部value变化时，更新内部状态
  useEffect(() => {
    if (!isEditing) {
      setInputValue(value);
    }
  }, [value, isEditing]);

  // 当外部isEditing变化时，确保内部状态同步
  useEffect(() => {
    if (externalIsEditing !== undefined && isEditing && textareaRef.current) {
      // 当从外部开启编辑模式时，重置输入值并聚焦
      setInputValue(value);
      // 使用setTimeout确保DOM更新后再执行选中操作
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.select();
        }
      }, 0);
    }
  }, [externalIsEditing, isEditing, value]);

  // 处理双击进入编辑模式
  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    e.stopPropagation();
    setInputValue(value); // 重置为原始值
    setInternalIsEditing(true);
    // 通知外部编辑状态改变
    if (onEditingChange) {
      onEditingChange(true);
    }

    // 使用setTimeout确保DOM更新后再执行选中操作
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }, 0);
  };

  // 处理输入变化并自动调整高度
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // 立即更新行数，不等待useEffect
    const newRows = calculateRows(newValue);
    setCalculatedRows(newRows);
    e.target.rows = newRows;

    // 立即调整高度
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // 编辑模式切换时重新计算行数
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const rows = calculateRows(inputValue);
      setCalculatedRows(rows);
      textareaRef.current.rows = rows;

      // 调整高度
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';

      // 设置溢出控制
      if (rows >= maxRows) {
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  }, [isEditing, inputValue, maxRows]);

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // 阻止默认的换行行为
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // 处理失焦
  const handleBlur = () => {
    handleSave();
  };

  // 行数计算方法（如果row计算的有问题，就改这个方法）
  const calculateRows = (text: string): number => {
    if (!text) return minRows;

    const lines = text.split('\n');
    let totalRows = 0;

    // 更精确的估算参数
    const baseCharsPerLine = 30;
    const chineseCharRatio = 3; // 中文字符宽度比例
    const englishCharRatio = 1.0;

    lines.forEach(line => {
      if (!line.trim()) {
        totalRows += 1;
        return;
      }

      // 估算字符宽度（考虑中英文混合）
      let effectiveLength = 0;
      for (const char of line) {
        // 简单判断中文字符（可以根据需要扩展）
        const isChinese = /[\u4e00-\u9fa5]/.test(char);
        effectiveLength += isChinese ? chineseCharRatio : englishCharRatio;
      }

      const lineRows = Math.max(1, Math.ceil(effectiveLength / baseCharsPerLine));
      totalRows += lineRows;
    });

    return Math.min(Math.max(totalRows, minRows), maxRows);
  };

  // 保存修改
  const handleSave = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      onChange(trimmedValue);
    }
    // 如果是内部控制，更新内部状态
    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
    // 通知外部编辑状态改变
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  // 取消编辑
  const handleCancel = () => {
    setInputValue(value); // 重置为原始值
    // 如果是内部控制，更新内部状态
    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
    // 通知外部编辑状态改变
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  // 阻止点击事件冒泡
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={cn('w-full relative', className)}>
      {!isEditing ? (
        <div
          className={cn(
            'w-full text-center line-clamp-2 cursor-text break-all',
            disabled && 'cursor-not-allowed opacity-60',
            textClassName
          )}
          onDoubleClick={handleDoubleClick}
          onClick={handleClick}
        >
          {value || placeholder}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className={cn(
            "max-h-16", // 限制高度
            'w-full text-i-normal bg-[#E5E5E6]',
            'rounded-sm px-2 py-0.5 border resize-none text-center',
            'whitespace-pre-wrap break-words focus:outline-none',
            'overflow-y-auto',
            textareaClassName
          )}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
          rows={calculatedRows}
          disabled={disabled}
          {...rest}
        />
      )}
    </div>
  );
};